import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Film, Tv, RadioTower, Search, Trash2, Link2, PackageOpen, CloudUpload, XCircle, FileUp, Youtube, RefreshCw } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { GlassCard, PageHeader, EmptyState, Spinner } from "../components/common";
import { TutorialHint } from "../components/TutorialHint";

const KIND_LABEL = { movie: "Filme", series: "Série", live: "Canal" };
const KIND_STYLE = {
  movie: "text-cyan-300 border-cyan-400/40 bg-cyan-400/10",
  series: "text-purple-300 border-purple-400/40 bg-purple-400/10",
  live: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10",
};

export default function Imports() {
  const [url, setUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [mig, setMig] = useState({ kind: "all", version: "dual" });
  const [migrating, setMigrating] = useState(false);
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ total: 0, items: [] });
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [ytUrl, setYtUrl] = useState("");
  const [ytBusy, setYtBusy] = useState(false);
  const fileRef = useRef(null);
  const debounce = useRef(null);

  const loadStats = () => api.get("/import/stats").then((r) => setStats(r.data)).catch(() => {});

  const load = useCallback((k = kind, query = q, skip = 0, append = false) => {
    setLoading(true);
    api.get("/import/streams", { params: { kind: k || undefined, q: query || undefined, skip, limit: 60 } })
      .then((r) => setData((prev) => append ? { total: r.data.total, items: [...prev.items, ...r.data.items] } : r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [kind, q]);

  useEffect(() => { loadStats(); load(); }, []);

  const loadJobs = () => api.get("/import/jobs").then((r) => setJobs(r.data)).catch(() => {});

  useEffect(() => {
    loadJobs();
    const t = setInterval(() => {
      if (jobs.some((j) => j.status === "running")) loadJobs();
    }, 5000);
    return () => clearInterval(t);
  }, [jobs]);

  const startMigrate = async () => {
    setMigrating(true);
    try {
      const { data: job } = await api.post("/import/migrate", { kind: mig.kind, default_version: mig.version });
      toast.success(`Migração iniciada: ${job.total} itens na fila`);
      loadJobs();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMigrating(false);
    }
  };

  const retryJob = async (j) => {
    try {
      await api.post("/import/yt", { url: j.source_url, version: j.default_version || "dual" });
      toast.success("Job recolocado na fila");
      loadJobs();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cancelJob = async (id) => {
    try {
      await api.post(`/import/jobs/${id}/cancel`);
      toast.success("Migração cancelada");
      loadJobs();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onSearch = (v) => {
    setQ(v);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(kind, v), 450);
  };

  const onKind = (k) => {
    setKind(k);
    load(k, q);
  };

  const doImport = async () => {
    if (!url.trim()) return;
    setImporting(true);
    setLastResult(null);
    try {
      const { data: r } = await api.post("/import/m3u-url", { url: url.trim() });
      setLastResult(r);
      toast.success(`Playlist importada: ${r.total} itens (${r.created} novos)`);
      setUrl("");
      loadStats();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImporting(false);
    }
  };

  const doImportFile = async (f) => {
    if (!f) return;
    setImporting(true);
    setLastResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: r } = await api.post("/import/m3u-file", fd);
      setLastResult(r);
      toast.success(`Arquivo importado: ${r.total} itens (${r.created} novos)`);
      loadStats();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImporting(false);
    }
  };

  const doYt = async () => {
    if (!ytUrl.trim()) return;
    setYtBusy(true);
    try {
      await api.post("/import/yt", { url: ytUrl.trim(), version: mig.version });
      toast.success("Download com yt-dlp iniciado — acompanhe na fila abaixo");
      setYtUrl("");
      loadJobs();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setYtBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/import/streams/${id}`);
      setData((prev) => ({ total: prev.total - 1, items: prev.items.filter((i) => i.id !== id) }));
      loadStats();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="imports-page">
      <PageHeader eyebrow="Curadoria" title="Importações" subtitle="Importe playlists M3U por link (Xtream/get.php) e links diretos de filmes, séries e canais — pelo painel ou direto no bot do Telegram." />

      <TutorialHint
        id="imports"
        title="Importando sua lista"
        steps={[
          "Cole o link da playlist (get.php?username=...&password=...&type=m3u_plus) e clique em Importar.",
          "O sistema baixa a lista, lê item por item e separa Filmes, Séries e Canais automaticamente.",
          "Itens repetidos são atualizados, nunca duplicados (dedup por URL do stream).",
          "No bot do Telegram: cole o link da playlist e o bot pergunta se quer subir só Filmes, só Séries ou TUDO.",
          "Também dá para importar arquivo .m3u direto do computador (botão Arquivo .m3u) e baixar vídeos do YouTube/sites com o Super-importador.",
          "Na migração, o sistema baixa um vídeo por vez e envia direto para a nuvem — cada vídeo vira 1 link M3U único.",
          "Para baixar os vídeos, use a lista com output=mpegts — links HLS (.m3u8) são pulados.",
          "Sem limite de itens: listas com centenas de milhares de entradas são processadas em lotes.",
        ]}
      />

      <GlassCard className="p-6 anim-fade-up" data-testid="import-url-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center shrink-0">
            <Link2 size={20} className="text-cyan-400" />
          </div>
          <div>
            <h3 className="font-display text-white font-semibold text-sm">Importar playlist por URL</h3>
            <p className="text-[11px] text-slate-500">Xtream / get.php / .m3u / .m3u8</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="import-url-input"
            className="input-dark flex-1 rounded-xl px-4 py-3 text-sm font-mono2"
            placeholder="http://servidor/get.php?username=...&password=...&type=m3u_plus&output=hls"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <button data-testid="import-url-btn" onClick={doImport} disabled={importing || !url.trim()} className="btn-neon px-6 py-3 rounded-xl text-sm flex items-center justify-center gap-2 shrink-0">
            {importing ? <Spinner /> : <><Download size={15} /> Importar lista</>}
          </button>
          <button data-testid="import-file-btn" onClick={() => fileRef.current?.click()} disabled={importing} className="px-4 py-3 rounded-xl text-sm border border-slate-700 text-slate-300 hover:border-cyan-400/40 hover:text-white transition-colors flex items-center justify-center gap-2 shrink-0">
            <FileUp size={15} /> Arquivo .m3u
          </button>
          <input ref={fileRef} data-testid="import-file-input" type="file" accept=".m3u,.m3u8,.txt" className="hidden" onChange={(e) => { doImportFile(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
        {importing && <p className="text-[11px] font-mono2 text-cyan-400 mt-3" data-testid="import-progress-msg">Baixando e processando item por item... listas grandes levam até 1 minuto.</p>}
        {lastResult && (
          <div className="mt-4 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-200" data-testid="import-result">
            ✅ {lastResult.total} itens lidos · {lastResult.created} novos · {lastResult.updated} atualizados —
            🎬 {lastResult.movies} filmes · 📺 {lastResult.series} séries · 📡 {lastResult.live} canais
          </div>
        )}
      </GlassCard>

      <GlassCard className="p-6 anim-fade-up anim-delay-1" data-testid="migrate-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-purple-400/10 border border-purple-400/25 flex items-center justify-center shrink-0">
            <CloudUpload size={20} className="text-purple-400" />
          </div>
          <div>
            <h3 className="font-display text-white font-semibold text-sm">Subir para o acervo (nuvem)</h3>
            <p className="text-[11px] text-slate-500">Baixa um vídeo por vez da lista, envia direto para a nuvem e cada vídeo vira 1 link M3U único no site</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <select data-testid="migrate-kind-select" className="input-dark rounded-xl px-3 py-2.5 text-sm" value={mig.kind} onChange={(e) => setMig({ ...mig, kind: e.target.value })}>
            <option value="all">Filmes + Séries (tudo)</option>
            <option value="movie">Só Filmes</option>
            <option value="series">Só Séries</option>
          </select>
          <select data-testid="migrate-version-select" className="input-dark rounded-xl px-3 py-2.5 text-sm" value={mig.version} onChange={(e) => setMig({ ...mig, version: e.target.value })}>
            <option value="dual">Dual Áudio</option>
            <option value="dublado">Dublado</option>
            <option value="legendado">Legendado</option>
            <option value="nacional">Nacional</option>
          </select>
          <button data-testid="migrate-start-btn" onClick={startMigrate} disabled={migrating} className="btn-violet rounded-xl px-4 py-2.5 text-sm flex items-center justify-center gap-2">
            {migrating ? <Spinner /> : "Iniciar migração"}
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">Os vídeos vão direto para a nuvem (pasta da sua conta). Use a lista com <span className="font-mono2 text-slate-300">output=mpegts</span> para baixar os vídeos.</p>
      </GlassCard>

      <GlassCard className="p-6 anim-fade-up anim-delay-1" data-testid="yt-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-rose-400/10 border border-rose-400/25 flex items-center justify-center shrink-0">
            <Youtube size={20} className="text-rose-400" />
          </div>
          <div>
            <h3 className="font-display text-white font-semibold text-sm">Super-importador (YouTube e sites de vídeo)</h3>
            <p className="text-[11px] text-slate-500">Cole um link de vídeo do YouTube ou de milhares de sites — o servidor baixa e publica direto no acervo, na pasta da sua conta</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="yt-url-input"
            className="input-dark flex-1 rounded-xl px-4 py-3 text-sm font-mono2"
            placeholder="https://youtube.com/watch?v=... ou link de vídeo de outro site"
            value={ytUrl}
            onChange={(e) => setYtUrl(e.target.value)}
          />
          <button data-testid="yt-start-btn" onClick={doYt} disabled={ytBusy || !ytUrl.trim()} className="btn-neon px-6 py-3 rounded-xl text-sm flex items-center justify-center gap-2 shrink-0">
            {ytBusy ? <Spinner /> : <><Youtube size={15} /> Baixar para o acervo</>}
          </button>
        </div>
      </GlassCard>

      {jobs.length > 0 && (
        <GlassCard className="p-6 anim-fade-up" data-testid="migrate-jobs-card">
          <h3 className="font-display text-white font-semibold text-sm mb-4">Migrações</h3>
          <div className="space-y-4">
            {jobs.map((j) => {
              const pct = j.total ? Math.round(((j.processed + j.failed_count) / j.total) * 100) : 0;
              const STATUS = { running: ["Rodando", "text-cyan-300"], queued: ["Na fila", "text-amber-300"], done: ["Concluída", "text-emerald-300"], canceled: ["Cancelada", "text-slate-400"], failed: ["Falhou", "text-rose-400"] };
              const [lbl, cls] = STATUS[j.status] || ["—", "text-slate-400"];
              return (
                <div key={j.id} className="p-4 rounded-xl bg-slate-950/50 border border-slate-800/60" data-testid={`migrate-job-${j.id}`}>
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <span className={`text-xs font-semibold ${cls}`} data-testid={`migrate-job-status-${j.id}`}>{lbl}</span>
                    <span className="text-[10px] font-mono2 uppercase tracking-wider text-slate-500">{j.kind_filter} · {j.default_version}</span>
                    <span className="text-xs text-slate-400 ml-auto font-mono2">{j.processed}/{j.total} ok · {j.failed_count} erros{(j.published_bytes || 0) > 0 ? ` · ${(j.published_bytes / 1e9).toFixed(2)} GB` : ""}</span>
                    {j.status === "running" && (
                      <button data-testid={`migrate-cancel-${j.id}`} onClick={() => cancelJob(j.id)} className="p-1.5 rounded-lg text-rose-400 hover:bg-rose-500/10 transition-colors" title="Cancelar migração">
                        <XCircle size={15} />
                      </button>
                    )}
                    {j.status === "failed" && j.kind === "yt" && (
                      <button data-testid={`job-retry-${j.id}`} onClick={() => retryJob(j)} className="p-1.5 rounded-lg text-slate-500 hover:text-emerald-300 hover:bg-emerald-400/10 transition-colors" title="Reiniciar download">
                        <RefreshCw size={15} />
                      </button>
                    )}
                  </div>
                  {j.current && (j.status === "running" || j.status === "queued") && (
                    <p className="text-[11px] font-mono2 text-cyan-300/80 mb-2 truncate" data-testid={`migrate-current-${j.id}`}>{j.current}</p>
                  )}
                  <div className="h-2 rounded-full bg-slate-800 overflow-hidden" data-testid={`migrate-progress-${j.id}`}>
                    <div className="h-full upload-progress-fill" style={{ width: `${pct}%` }} />
                  </div>
                  {j.error && <p className="text-[11px] text-rose-300 mt-2">{j.error}</p>}
                  {j.errors?.length > 0 && <p className="text-[10px] text-slate-500 mt-2 font-mono2 line-clamp-2">Últimos erros: {j.errors.join(" · ")}</p>}
                </div>
              );
            })}
          </div>
        </GlassCard>
      )}

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="import-stats">
          {[
            { label: "Total importado", value: stats.total, icon: PackageOpen, accent: "text-white" },
            { label: "Filmes", value: stats.movie, icon: Film, accent: "text-cyan-400" },
            { label: "Séries", value: stats.series, icon: Tv, accent: "text-purple-400" },
            { label: "Canais", value: stats.live, icon: RadioTower, accent: "text-emerald-400" },
          ].map((c) => (
            <GlassCard key={c.label} className="p-4 glass-hover">
              <c.icon size={17} className={c.accent} />
              <p className="font-display text-xl font-bold text-white mt-2">{c.value}</p>
              <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 mt-1">{c.label}</p>
            </GlassCard>
          ))}
        </div>
      )}

      <GlassCard className="p-6 anim-fade-up anim-delay-1" data-testid="imported-streams-card">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
          <div className="flex gap-2">
            {[["", "Todos"], ["movie", "Filmes"], ["series", "Séries"], ["live", "Canais"]].map(([k, lbl]) => (
              <button
                key={k}
                data-testid={`filter-kind-${k || "all"}`}
                onClick={() => onKind(k)}
                className={`px-3.5 py-2 rounded-lg text-xs font-semibold transition-colors border ${kind === k ? "bg-cyan-400/15 text-cyan-300 border-cyan-400/30" : "text-slate-400 border-slate-800 hover:text-white"}`}
              >
                {lbl}
              </button>
            ))}
          </div>
          <div className="relative sm:ml-auto sm:w-72">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="imports-search-input"
              className="input-dark w-full rounded-xl pl-10 pr-4 py-2.5 text-sm"
              placeholder="Buscar item..."
              value={q}
              onChange={(e) => onSearch(e.target.value)}
            />
          </div>
        </div>

        {loading && data.items.length === 0 ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : data.items.length === 0 ? (
          <EmptyState icon={Download} title="Nada importado ainda" subtitle="Cole o link da playlist acima ou envie pelo bot do Telegram." testid="imports-empty" />
        ) : (
          <>
            <p className="text-[11px] font-mono2 text-slate-500 mb-3" data-testid="imports-count">{data.total} itens · mostrando {data.items.length}</p>
            <div className="space-y-2" data-testid="imports-list">
              {data.items.map((s) => (
                <div key={s.id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-950/50 border border-slate-800/60" data-testid={`import-item-${s.id}`}>
                  {s.logo ? (
                    <img src={s.logo} alt="" className="w-9 h-12 rounded-lg object-cover shrink-0" loading="lazy" />
                  ) : (
                    <div className="w-9 h-12 rounded-lg bg-slate-800 flex items-center justify-center shrink-0">
                      {s.kind === "series" ? <Tv size={14} className="text-slate-600" /> : s.kind === "live" ? <RadioTower size={14} className="text-slate-600" /> : <Film size={14} className="text-slate-600" />}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white font-medium truncate">{s.name}</p>
                    <p className="text-[10px] text-slate-500 truncate font-mono2">{s.group || s.stream_url}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-md border text-[10px] font-mono2 font-semibold uppercase tracking-wider shrink-0 ${KIND_STYLE[s.kind]}`}>
                    {KIND_LABEL[s.kind]}
                  </span>
                  <button data-testid={`import-delete-${s.id}`} onClick={() => remove(s.id)} className="p-1.5 rounded-lg text-slate-600 hover:text-rose-400 transition-colors shrink-0">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            {data.items.length < data.total && (
              <button
                data-testid="imports-load-more"
                onClick={() => load(kind, q, data.items.length, true)}
                className="btn-ghost-dark w-full mt-4 py-3 rounded-xl text-sm text-slate-300"
              >
                Carregar mais ({data.total - data.items.length} restantes)
              </button>
            )}
          </>
        )}
      </GlassCard>
    </div>
  );
}
